import { SUBSTATUS_LABELS } from '../../utils/catalogs';
import { ATTENTION_REASON, STALLED_AFTER_DAYS } from './attentionConstants';

// `partial` and `complete` say how much of the delivery failed, not why.
const FAILED_WITHOUT_REASON = ['not_specified', 'partial', 'complete'];

const daysLabel = (days) => (days === 1 ? '1 día' : `${days} días`);

/**
 * Days elapsed since `value`, rounded step by step exactly like
 * `formatRelativeTime`, so this column and "Último movimiento" never disagree
 * about the same instant.
 *
 * @returns {number|null} null when there is no usable date.
 */
export const daysSince = (value, now = Date.now()) => {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return null;
  const hours = Math.round(Math.round((now - then) / 60000) / 60);
  return Math.max(0, Math.round(hours / 24));
};

const failedDetail = (substatus) => {
  if (!FAILED_WITHOUT_REASON.includes(substatus) && SUBSTATUS_LABELS[substatus]) {
    return SUBSTATUS_LABELS[substatus];
  }
  return substatus === 'partial' ? 'Parcial, sin motivo informado' : 'Sin motivo informado';
};

/**
 * Why a row of the attention queue is there, one entry per reason, in the order
 * they read best: the outcome first, then what is missing.
 *
 * Every reason carries its `detail` — the column used to print "Sin moverse"
 * with no way to tell since when, and a failed or returning shipment printed
 * nothing at all, because only two of the four reasons had a chip.
 *
 * @param {object} row - A row of `api/deliveries/attention/`.
 * @returns {Array<{key: string, label: string, detail: string|null, title: string, tone: string}>}
 */
export const attentionReasonsOf = (row, now = Date.now()) => {
  const reasons = [];

  if (row.status === 'failed') {
    const detail = failedDetail(row.substatus);
    reasons.push({
      key: ATTENTION_REASON.FAILED,
      label: 'Fallida',
      detail,
      title: `La entrega falló: ${detail}`,
      tone: 'danger',
    });
  }

  if (row.status === 'returning') {
    // `not_specified` has no label, and "No aplica" would tell nothing.
    const substatus = SUBSTATUS_LABELS[row.substatus] || null;
    reasons.push({
      key: ATTENTION_REASON.RETURNING,
      label: 'En devolución',
      detail: substatus,
      title: substatus ? `Va de vuelta: ${substatus}` : 'El envío va de vuelta',
      tone: 'warning',
    });
  }

  if (row.is_stalled) {
    const days = daysSince(row.unchanged_since, now);
    const neverMoved = !row.last_movement;
    let detail = null;
    if (days !== null) {
      detail = neverMoved
        ? `${daysLabel(days)} sin movimientos desde su creación`
        : `${daysLabel(days)} sin cambiar de estado`;
    }
    reasons.push({
      key: ATTENTION_REASON.STALLED,
      label: 'Sin moverse',
      detail,
      title: `Se marca cuando un envío abierto pasa más de ${STALLED_AFTER_DAYS} días en el mismo estado`,
      tone: 'warning',
    });
  }

  if (row.is_untracked) {
    reasons.push({
      key: ATTENTION_REASON.WITHOUT_TRACKING,
      label: 'Sin tracking',
      detail: null,
      title: 'Ni el envío ni ninguno de sus bultos tiene número de seguimiento',
      tone: 'neutral',
    });
  }

  return reasons;
};
